import { Button, IconButton, Tooltip } from "@mui/material";
import DeleteModal from "components/Modals/DeleteModal";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { usePermission } from "hooks/usePermissions";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit, AiFillFolder } from "react-icons/ai";
import { MdInsertChartOutlined } from "react-icons/md";
import { FaPlusCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import StudentModal from "./StudentModal";
import { eventClick } from "components/helper";
import DisplayDate from "components/DisplayDate";
import { RefreshData } from "components/helper";

const University = ({ color = "light" }) => {
  const navigate = useNavigate();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [{ uniData, token, isUniLoading }, { refetchUniData }] = useKothar();
  const canViewDocuments = usePermission(P.UNIVERSITY_DOCUMENT.READ);
  const canViewAdmissionSummary = usePermission(P.ADMISSIONS.READ);

  useEffect(() => {
    if (!uniData?.length) {
      refetchUniData();
    }
  }, []);

  const handleDeleteData = () => {
    deleteData(
      `${API_URL}/university/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchUniData();
      },
      token
    );
  };

  const [studentModal, setStudentModal] = useState({
    state: false,
    data: null,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue}>
            <div className="flex items-center">
              <div className=" mr-2">{renderedCellValue}</div>
            </div>
          </Tooltip>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 150,
      },
      {
        accessorKey: "abn",
        header: "CRICOS/RTO Code",
        size: 150,
      },
      {
        accessorKey: "contactPerson",
        header: "Contact Person",
        size: 150,
      },

      {
        accessorKey: "state",
        header: "State",
        size: 150,
      },
      {
        accessorKey: "updatedDate",
        header: "Last Updated",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div className="flex items-center">
              <DisplayDate value={row?.original?.updatedDate} />
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row, renderedCellValue }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              {canViewDocuments && false && (
                <Tooltip title="Documents" arrow>
                  <IconButton onClick={() => navigate(`/admin/university/${item?.id}/documents`)}>
                    <AiFillFolder className="text-primary cursor-pointer" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit University/College" arrow>
                <IconButton
                  onClick={() =>
                    navigate(`/admin/university/edit/${item?.id}`, {
                      state: { item },
                    })
                  }
                >
                  <AiFillEdit className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete University/College" arrow>
                <IconButton
                  onClick={() =>
                    setOpenConfirmationModal({
                      state: true,
                      id: row?.original?.id,
                    })
                  }
                >
                  <AiFillDelete className="text-red-600 cursor-pointer" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [canViewDocuments]
  );

  const table = useMaterialReactTable({
    columns,
    data: uniData || [],
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh", // Set max height
        minHeight: "70vh", // Set max height

        overflowY: "auto", // Enable vertical scrolling
      },
    },
    state: {
      isLoading: isUniLoading, //cell skeletons and loading overlay
      showProgressBars: isUniLoading, //progress bars while refetching
      isSaving: isUniLoading, //progress bars and save button spinners
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Check if the click was on an action button or its child

        if (!eventClick(event)) {
          navigate(`/admin/university/edit/${row?.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                Universities/Colleges ({uniData?.length}){" "}
                <RefreshData refreshData={refetchUniData} />
              </h3>
              {/* <SearchField
                {...{ type: "University", searchText, setSearchText }}
              /> */}
              <div className="flex items-center gap-2">
                {canViewAdmissionSummary && (
                  <Button
                    variant="outlined"
                    startIcon={<MdInsertChartOutlined />}
                    component={Link}
                    to="/admin/university/report"
                  >
                    Admission Summary
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  to="/admin/university/add"
                >
                  Add University/College
                </Button>
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="University"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => handleDeleteData()}
        />
      )}
      {studentModal?.state && (
        <StudentModal
          open={studentModal?.state}
          data={studentModal?.data}
          uni={studentModal?.uni}
          handleCancel={() => setStudentModal({ state: false, data: null, uni: null })}
        />
      )}
    </div>
  );
};

export default University;
